import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  AdminMerchandiseRequest,
  createAdminMerchandise,
  deleteAdminMerchandise,
  deleteMerchandiseImage,
  getAdminMerchandise,
  getAdminMerchandiseItem,
  restoreAdminMerchandise,
  setMerchandisePrimaryImage,
  updateAdminMerchandise,
  uploadMerchandiseImage,
} from '../../common/Rest/Services/AdminService/AdminService';
import {
  Merchandise,
  MerchandiseCategory,
  merchandiseCategoryLabels,
} from '../../common/Rest/Services/ShopService';
import { BOOK_COVER_PLACEHOLDER, toAbsoluteUrl } from '../../common/Utils/BookImages';
import ROUTES from '../../common/Routing/Routes';

interface MerchandiseDraft {
  name: string;
  description: string;
  details: string;
  category: MerchandiseCategory;
  price: string;
  stockCount: string;
}

const emptyDraft: MerchandiseDraft = {
  name: '',
  description: '',
  details: '',
  category: MerchandiseCategory.Bookmark,
  price: '',
  stockCount: '1',
};

const toDraft = (item: Merchandise): MerchandiseDraft => ({
  name: item.name,
  description: item.description || '',
  details: item.details || '',
  category: item.category,
  price: String(item.price ?? ''),
  stockCount: String(item.stockCount ?? 0),
});

const toRequest = (draft: MerchandiseDraft): AdminMerchandiseRequest => ({
  name: draft.name.trim(),
  description: draft.description,
  details: draft.details,
  category: draft.category,
  price: Number(draft.price) || 0,
  stockCount: Math.max(0, Math.trunc(Number(draft.stockCount) || 0)),
});

const thumbUrl = (item: Merchandise) => {
  const image = item.primaryImage || item.images[0];
  return toAbsoluteUrl(image?.thumbUrl || image?.url) || BOOK_COVER_PLACEHOLDER;
};

function AdminShopPanel() {
  const [items, setItems] = useState<Merchandise[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Merchandise | null>(null);
  const [draft, setDraft] = useState<MerchandiseDraft>(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const page = await getAdminMerchandise(search.trim());
      setItems(page.items);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timeout = window.setTimeout(loadItems, 250);
    return () => window.clearTimeout(timeout);
  }, [loadItems]);

  const replaceItem = (updated: Merchandise) => {
    setSelected((current) => (current?.id === updated.id ? updated : current));
    setItems((current) => {
      const exists = current.some((item) => item.id === updated.id);
      return exists ? current.map((item) => (item.id === updated.id ? updated : item)) : [updated, ...current];
    });
  };

  const selectItem = (item: Merchandise | null) => {
    setSelected(item);
    setDraft(item ? toDraft(item) : emptyDraft);
    setFiles([]);
    setMessage(null);
  };

  const updateDraft = (patch: Partial<MerchandiseDraft>) => setDraft((current) => ({ ...current, ...patch }));

  const handleSave = async () => {
    if (!draft.name.trim()) {
      setMessage({ severity: 'error', text: 'Вкажіть назву товару.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const saved = selected
        ? await updateAdminMerchandise(selected.id, toRequest(draft))
        : await createAdminMerchandise(toRequest(draft));
      replaceItem(saved);
      setSelected(saved);
      setDraft(toDraft(saved));
      setMessage({
        severity: 'success',
        text: selected ? 'Товар збережено.' : 'Товар створено. Тепер додайте фото.',
      });
    } catch {
      setMessage({ severity: 'error', text: 'Не вдалося зберегти товар.' });
    } finally {
      setSaving(false);
    }
  };

  const reloadSelected = async (id: number) => {
    replaceItem(await getAdminMerchandiseItem(id));
  };

  const handleUpload = async () => {
    if (!selected || files.length === 0) return;

    setUploading(true);
    setMessage(null);
    const hasPrimary = selected.images.some((image) => image.isPrimary);
    try {
      for (let index = 0; index < files.length; index++) {
        await uploadMerchandiseImage(selected.id, files[index], !hasPrimary && index === 0);
      }
      setFiles([]);
      await reloadSelected(selected.id);
      setMessage({ severity: 'success', text: files.length > 1 ? `Завантажено фото: ${files.length}.` : 'Фото завантажено.' });
    } catch {
      setMessage({ severity: 'error', text: 'Не вдалося завантажити фото. Підтримуються JPEG, PNG і WebP до 8 МБ.' });
      await reloadSelected(selected.id);
    } finally {
      setUploading(false);
    }
  };

  const handleSetPrimary = async (imageId: number) => {
    if (!selected) return;
    await setMerchandisePrimaryImage(imageId);
    await reloadSelected(selected.id);
  };

  const handleDeleteImage = async (imageId: number) => {
    if (!selected || !window.confirm('Видалити це фото?')) return;
    await deleteMerchandiseImage(imageId);
    await reloadSelected(selected.id);
  };

  const handleToggleDeleted = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      if (selected.isDeleted) {
        await restoreAdminMerchandise(selected.id);
      } else {
        await deleteAdminMerchandise(selected.id);
      }
      replaceItem({ ...selected, isDeleted: !selected.isDeleted });
      setMessage({ severity: 'success', text: selected.isDeleted ? 'Товар повернуто в крамничку.' : 'Товар приховано з крамнички.' });
    } catch {
      setMessage({ severity: 'error', text: 'Не вдалося змінити видимість товару.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-books-layout">
      <aside className="admin-book-list">
        <div className="admin-book-toolbar">
          <TextField
            label="Пошук товару"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="small"
            placeholder="Назва або ID"
          />
          <Button variant="contained" onClick={() => selectItem(null)}>Новий товар</Button>
        </div>

        {loading ? (
          <CircularProgress />
        ) : items.length ? (
          <div className="admin-book-rows">
            {items.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`admin-book-row ${selected?.id === item.id ? 'selected' : ''}`}
                onClick={() => selectItem(item)}
              >
                <img src={thumbUrl(item)} alt="" />
                <span>
                  <strong>{item.name}</strong>
                  <small>
                    {merchandiseCategoryLabels[item.category]} · {item.stockCount} шт. · {item.price} грн
                    {item.isDeleted ? ' · приховано' : ''}
                  </small>
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="admin-empty">Товарів ще немає.</p>
        )}
      </aside>

      <article className="admin-book-editor">
        <header className="admin-order-header">
          <div>
            <p className="admin-kicker">{selected ? `Товар #${selected.id}` : 'Новий товар'}</p>
            <h2>{selected ? selected.name : 'Додати товар у крамничку'}</h2>
            {selected && !selected.isDeleted && (
              <a href={`${ROUTES.SHOP}/${selected.id}`} target="_blank" rel="noopener noreferrer">
                Відкрити на сайті
              </a>
            )}
          </div>
          {selected && (
            <span className={`admin-status ${selected.isDeleted ? 'status-Cancelled' : 'status-Completed'}`}>
              {selected.isDeleted ? 'Приховано' : 'У крамничці'}
            </span>
          )}
        </header>

        {message && <Alert severity={message.severity}>{message.text}</Alert>}

        <div className="admin-book-form">
          <TextField label="Назва" value={draft.name} onChange={(event) => updateDraft({ name: event.target.value })} required />
          <TextField
            select
            label="Категорія"
            value={draft.category}
            onChange={(event) => updateDraft({ category: event.target.value as MerchandiseCategory })}
          >
            {Object.values(MerchandiseCategory).map((category) => (
              <MenuItem key={category} value={category}>{merchandiseCategoryLabels[category]}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Ціна, грн"
            type="number"
            value={draft.price}
            onChange={(event) => updateDraft({ price: event.target.value })}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
          <TextField
            label="Кількість в наявності"
            type="number"
            value={draft.stockCount}
            onChange={(event) => updateDraft({ stockCount: event.target.value })}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
          <TextField
            className="admin-book-field-wide"
            label="Опис"
            value={draft.description}
            onChange={(event) => updateDraft({ description: event.target.value })}
            multiline
            minRows={4}
          />
          <TextField
            className="admin-book-field-wide"
            label="Характеристики (розмір, матеріал тощо)"
            value={draft.details}
            onChange={(event) => updateDraft({ details: event.target.value })}
            multiline
            minRows={2}
          />
        </div>

        <div className="admin-actions">
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Зберігаємо...' : selected ? 'Зберегти товар' : 'Створити товар'}
          </Button>
          {selected && (
            <Button color={selected.isDeleted ? 'primary' : 'error'} variant="outlined" onClick={handleToggleDeleted} disabled={saving}>
              {selected.isDeleted ? 'Повернути в крамничку' : 'Приховати з крамнички'}
            </Button>
          )}
        </div>

        {selected && (
          <section className="admin-book-images">
            <h3>Фото товару</h3>
            {selected.images.length > 0 ? (
              <div className="admin-shop-images">
                {selected.images.map((image) => (
                  <figure key={image.id} className={image.isPrimary ? 'primary' : ''}>
                    <img src={toAbsoluteUrl(image.thumbUrl || image.url)} alt="" />
                    <figcaption>
                      <Tooltip title={image.isPrimary ? 'Головне фото' : 'Зробити головним'}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Зробити головним"
                            onClick={() => handleSetPrimary(image.id)}
                            disabled={image.isPrimary}
                          >
                            {image.isPrimary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Видалити фото">
                        <IconButton size="small" aria-label="Видалити фото" onClick={() => handleDeleteImage(image.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <p className="admin-empty">Фото ще немає. Перше завантажене фото стане головним.</p>
            )}

            <div className="admin-actions">
              <Button variant="outlined" component="label">
                {files.length ? `Вибрано фото: ${files.length}` : 'Вибрати фото'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  hidden
                  onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
                />
              </Button>
              <Button variant="contained" onClick={handleUpload} disabled={files.length === 0 || uploading}>
                {uploading ? 'Завантажуємо...' : 'Завантажити'}
              </Button>
            </div>
          </section>
        )}
      </article>
    </section>
  );
}

export default AdminShopPanel;
